import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

interface BaseCampo {
  label: ReactNode;
  error?: string | undefined;
  ayuda?: ReactNode;
}

function describedBy(id: string, error?: string, ayuda?: ReactNode): string | undefined {
  const ids = [ayuda ? `${id}-ayuda` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

function Mensajes({ id, error, ayuda }: { id: string; error?: string | undefined; ayuda?: ReactNode }) {
  return (
    <>
      {ayuda ? (
        <span id={`${id}-ayuda`} className="t-campo__ayuda">
          {ayuda}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-error`} className="t-campo__error" role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
}

export type TextFieldProps = BaseCampo & InputHTMLAttributes<HTMLInputElement>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField({ label, error, ayuda, id, className, ...rest }, ref) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <div className="t-campo">
      <label htmlFor={cid}>
        {label}
        {rest.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        ref={ref}
        id={cid}
        className={['t-input', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(cid, error, ayuda)}
        {...rest}
      />
      <Mensajes id={cid} error={error} ayuda={ayuda} />
    </div>
  );
});

export type TextAreaFieldProps = BaseCampo & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField({ label, error, ayuda, id, ...rest }, ref) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <div className="t-campo">
      <label htmlFor={cid}>
        {label}
        {rest.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <textarea ref={ref} id={cid} className="t-textarea" aria-invalid={error ? true : undefined} aria-describedby={describedBy(cid, error, ayuda)} {...rest} />
      <Mensajes id={cid} error={error} ayuda={ayuda} />
    </div>
  );
});

export interface Opcion {
  valor: string;
  etiqueta: string;
}

export type SelectFieldProps = BaseCampo & SelectHTMLAttributes<HTMLSelectElement> & { opciones: Opcion[]; vacio?: string };

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField({ label, error, ayuda, id, opciones, vacio, ...rest }, ref) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <div className="t-campo">
      <label htmlFor={cid}>
        {label}
        {rest.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <select ref={ref} id={cid} className="t-select" aria-invalid={error ? true : undefined} aria-describedby={describedBy(cid, error, ayuda)} {...rest}>
        {vacio !== undefined ? <option value="">{vacio}</option> : null}
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
      </select>
      <Mensajes id={cid} error={error} ayuda={ayuda} />
    </div>
  );
});

export type CheckboxFieldProps = Omit<BaseCampo, 'ayuda'> & InputHTMLAttributes<HTMLInputElement>;

export const CheckboxField = forwardRef<HTMLInputElement, CheckboxFieldProps>(function CheckboxField({ label, error, id, ...rest }, ref) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <div className="t-campo">
      <div className="t-check">
        <input ref={ref} type="checkbox" id={cid} aria-invalid={error ? true : undefined} aria-describedby={error ? `${cid}-error` : undefined} {...rest} />
        <label htmlFor={cid}>{label}</label>
      </div>
      <Mensajes id={cid} error={error} />
    </div>
  );
});
